import React, {useState, useMemo, useEffect, useRef} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'
import {Accordion, Box, Skeleton, useSlotRecipe, useAccordionItemContext} from '@chakra-ui/react3'
import {ChevronDownIcon} from '../../../../../leap/components/icons'
import {RECOMMENDED_SPARE_PARTS_TYPE} from '../../../../../constants'
import {hasWhatsIncluded} from './utils'
import productDetailsAccordionRecipe from './index.recipe'
import ModuleContainer from '../../../../../leap/components/module-container'
import useOcapiSite from '../../../../../hooks/useOcapiSite'
import lazyLoadable from '../../../../../utils/lazy-loadable'

// panel content mounts only while open, so each panel loads with forceLoad
const fallback = <Skeleton height="25vh" width="100%" />
const Specifications = lazyLoadable(() => import('./specifications'), {fallback})
const WhatsIncluded = lazyLoadable(() => import('./whats-included'), {fallback})
const HowToUse = lazyLoadable(() => import('./how-to-use'), {fallback})
const SpareParts = lazyLoadable(() => import('./spare-parts'), {fallback})

/**
 * @param {Object} props
 * @param {function({expanded: boolean}): React.ReactNode} props.render
 * @returns {React.ReactNode|null}
 */

const MountOnOpen = ({render}) => {
    const {expanded} = useAccordionItemContext()
    return expanded ? render({expanded}) : null
}

MountOnOpen.propTypes = {
    render: PropTypes.func.isRequired
}

const ProductDetailsAccordion = ({product, onRecommendedProductClick}) => {
    const recipe = useSlotRecipe({recipe: productDetailsAccordionRecipe})
    const styles = useMemo(() => recipe(), [recipe])
    const {brightcoveEnabled} = useOcapiSite()
    // DOM node per section value, used to scroll a newly opened section into view.
    const itemRefs = useRef({})
    const [openValue, setOpenValue] = useState(null)

    useEffect(() => {
        if (!openValue) return
        const node = itemRefs.current[openValue]
        if (node && typeof node.scrollIntoView === 'function') {
            node.scrollIntoView({behavior: 'smooth', block: 'start'})
        }
    }, [openValue])

    const handleValueChange = (details) => {
        setOpenValue(details?.value?.[0] ?? null)
    }

    const _hasWhatsIncluded = useMemo(() => hasWhatsIncluded(product), [product])
    const hasSpareParts = product?.recommendations?.some(
        ({recommendationType}) => recommendationType.value === RECOMMENDED_SPARE_PARTS_TYPE
    )

    const hasFaqs =
        (product?.c_assemblyVideos?.[0] && brightcoveEnabled) ||
        product?.c_maintenance ||
        product?.c_documents

    const items = useMemo(() => {
        const sections = [
            {
                value: 'specifications',
                title: (
                    <FormattedMessage
                        id="pdp.module.specifications"
                        defaultMessage="Specifications"
                    />
                ),
                content: () => <Specifications forceLoad product={product} />
            }
        ]

        if (_hasWhatsIncluded) {
            sections.push({
                value: 'whats-included',
                title: (
                    <FormattedMessage
                        id="pdp.module.specifications.included"
                        defaultMessage="What's included"
                    />
                ),
                content: () => <WhatsIncluded forceLoad product={product} />
            })
        }
        if (hasFaqs) {
            sections.push({
                value: 'how-to-use',
                title: (
                    <FormattedMessage
                        id="product_detail.after_sales.documents"
                        defaultMessage="How to use / Documents"
                    />
                ),
                content: ({expanded}) => (
                    <HowToUse forceLoad product={product} isExpanded={expanded} />
                )
            })
        }

        if (hasSpareParts) {
            sections.push({
                value: 'spare-parts',
                title: (
                    <FormattedMessage
                        id="product_detail.sections.spareParts"
                        defaultMessage="Spare Parts"
                    />
                ),
                content: () => (
                    <SpareParts
                        forceLoad
                        product={product}
                        onRecommendedProductClick={onRecommendedProductClick}
                    />
                )
            })
        }
        return sections
    }, [product, _hasWhatsIncluded, hasFaqs, hasSpareParts, onRecommendedProductClick])

    if (!product) return null

    return (
        <ModuleContainer
            moduleType="product-details-accordion"
            background="none"
            spacing="none"
            css={styles.moduleContainer}
        >
            {items.length > 0 && (
                <Accordion.Root
                    unstyled
                    collapsible
                    multiple={false}
                    css={styles.accordion}
                    onValueChange={handleValueChange}
                >
                    {items.map((item) => (
                        <Accordion.Item
                            key={item.value}
                            value={item.value}
                            css={styles.item}
                            ref={(node) => {
                                itemRefs.current[item.value] = node
                            }}
                        >
                            {/* WAI-ARIA accordion structure is <heading><button> — the trigger is a
                                button, so the heading must wrap it (the two cannot be one element). */}
                            <Box as="h2" css={styles.heading}>
                                <Accordion.ItemTrigger
                                    css={styles.trigger}
                                    data-testid={item.testid}
                                >
                                    <Box as="span" css={styles.itemHeadline}>
                                        {item.title}
                                    </Box>

                                    <Accordion.ItemIndicator css={styles.indicator}>
                                        <ChevronDownIcon />
                                    </Accordion.ItemIndicator>
                                </Accordion.ItemTrigger>
                            </Box>

                            <Accordion.ItemContent css={styles.content}>
                                <Box css={styles.contentInner}>
                                    <MountOnOpen render={item.content} />
                                </Box>
                            </Accordion.ItemContent>
                        </Accordion.Item>
                    ))}
                </Accordion.Root>
            )}
        </ModuleContainer>
    )
}

ProductDetailsAccordion.propTypes = {
    product: PropTypes.object,
    onRecommendedProductClick: PropTypes.func
}

export default ProductDetailsAccordion
